import React, { useState } from 'react';
import { X, FileText, Calendar, Clock, MapPin, Briefcase, DollarSign, Star, ChevronRight } from 'lucide-react';

const API = 'https://talentflow-backend-production-09dd.up.railway.app/api';

const TIME_SLOTS = [
  '9:00 AM - 10:00 AM',
  '10:00 AM - 11:00 AM',
  '11:00 AM - 12:00 PM',
  '12:00 PM - 1:00 PM',
  '1:00 PM - 2:00 PM',
  '2:00 PM - 3:00 PM',
  '3:00 PM - 4:00 PM',
  '4:00 PM - 5:00 PM',
  '5:00 PM - 6:00 PM',
];

function ScheduleModal({ candidate, token, onClose }) {
  const [step, setStep] = useState('calendar');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const today = new Date();
  const dates = Array.from({length: 14}, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i + 1);
    return d;
  }).filter(d => d.getDay() !== 0 && d.getDay() !== 6);

  const toggleSlot = (slot) => {
    if (selectedSlots.includes(slot)) {
      setSelectedSlots(selectedSlots.filter(s => s !== slot));
    } else if (selectedSlots.length < 3) {
      setSelectedSlots([...selectedSlots, slot]);
    }
  };

  const submitSlots = async () => {
    if (!selectedDate || selectedSlots.length === 0) return;
    setSubmitting(true);
    try {
      await fetch(`${API}/candidates/${candidate._id}/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          date: selectedDate.toISOString().split('T')[0],
          slots: selectedSlots
        })
      });
      setDone(true);
    } catch {}
    setSubmitting(false);
  };

  const formatDate = (d) => d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });

  if (done) return (
    <div style={{textAlign:'center',padding:'40px'}}>
      <div style={{fontSize:'56px',marginBottom:'16px'}}>🎉</div>
      <h3 style={{color:'#1e293b',fontSize:'20px',fontWeight:'700',margin:'0 0 8px'}}>Slots Sent!</h3>
      <p style={{color:'#64748b',fontSize:'14px',margin:'0 0 24px'}}>Vendor will choose a slot and confirm the meeting.</p>
      <button onClick={onClose} style={{padding:'10px 24px',background:'linear-gradient(135deg,#6366f1,#8b5cf6)',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'14px',fontWeight:'600',fontFamily:'inherit'}}>Done</button>
    </div>
  );

  return (
    <div>
      <h3 style={{color:'#1e293b',fontSize:'18px',fontWeight:'700',margin:'0 0 20px'}}>📅 Schedule Interview</h3>
      <p style={{color:'#64748b',fontSize:'13px',margin:'0 0 16px'}}>Select a date and up to 3 time slots. Vendor will confirm availability.</p>

      {/* Date picker */}
      <div style={{marginBottom:'20px'}}>
        <label style={{color:'#64748b',fontSize:'12px',fontWeight:'600',display:'block',marginBottom:'8px'}}>SELECT DATE</label>
        <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
          {dates.slice(0,10).map(d => (
            <div key={d.toISOString()} onClick={() => setSelectedDate(d)}
              style={{padding:'8px 14px',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600',
                background: selectedDate?.toDateString() === d.toDateString() ? 'linear-gradient(135deg,#6366f1,#8b5cf6)' : '#f8fafc',
                color: selectedDate?.toDateString() === d.toDateString() ? 'white' : '#1e293b',
                border: selectedDate?.toDateString() === d.toDateString() ? 'none' : '1px solid #e2e8f0'
              }}>
              {formatDate(d)}
            </div>
          ))}
        </div>
      </div>

      {/* Time slots */}
      {selectedDate && (
        <div style={{marginBottom:'24px'}}>
          <label style={{color:'#64748b',fontSize:'12px',fontWeight:'600',display:'block',marginBottom:'8px'}}>
            SELECT UP TO 3 TIME SLOTS ({selectedSlots.length}/3 selected)
          </label>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px'}}>
            {TIME_SLOTS.map(slot => (
              <div key={slot} onClick={() => toggleSlot(slot)}
                style={{padding:'10px',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'600',textAlign:'center',
                  background: selectedSlots.includes(slot) ? 'linear-gradient(135deg,#6366f1,#8b5cf6)' : '#f8fafc',
                  color: selectedSlots.includes(slot) ? 'white' : '#1e293b',
                  border: selectedSlots.includes(slot) ? 'none' : '1px solid #e2e8f0',
                  opacity: !selectedSlots.includes(slot) && selectedSlots.length >= 3 ? 0.4 : 1
                }}>
                {slot}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{display:'flex',gap:'12px'}}>
        <button onClick={onClose} style={{padding:'11px 20px',background:'#f1f5f9',color:'#64748b',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'14px',fontFamily:'inherit'}}>Cancel</button>
        <button onClick={submitSlots} disabled={!selectedDate || selectedSlots.length === 0 || submitting}
          style={{flex:1,padding:'11px',background:'linear-gradient(135deg,#6366f1,#8b5cf6)',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'14px',fontWeight:'600',fontFamily:'inherit',opacity:(!selectedDate || selectedSlots.length === 0) ? 0.5 : 1}}>
          {submitting ? 'Sending...' : `Send ${selectedSlots.length} Slot${selectedSlots.length !== 1 ? 's' : ''} to Vendor →`}
        </button>
      </div>
    </div>
  );
}

export default function CandidateProfile({ candidate, token, onClose, userRole }) {
  const [view, setView] = useState('profile');

  const avatar = (name, size=56) => {
    const colors = ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981'];
    const c = colors[name?.charCodeAt(0) % colors.length];
    return <div style={{width:`${size}px`,height:`${size}px`,borderRadius:'50%',background:c,display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontWeight:'700',fontSize:`${size/2.5}px`,flexShrink:0}}>{name?.charAt(0).toUpperCase()}</div>;
  };

  const scoreColor = (score) => score >= 8 ? '#16a34a' : score >= 6 ? '#ca8a04' : '#dc2626';
  const scoreBg = (score) => score >= 8 ? '#dcfce7' : score >= 6 ? '#fef9c3' : '#fee2e2';

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}} onClick={onClose}>
      <div style={{background:'white',borderRadius:'16px',width:'100%',maxWidth:'600px',maxHeight:'90vh',overflowY:'auto',boxShadow:'0 40px 80px rgba(0,0,0,0.3)'}} onClick={e=>e.stopPropagation()}>

        {/* Header */}
        <div style={{padding:'24px',borderBottom:'1px solid #f1f5f9',display:'flex',justifyContent:'space-between',alignItems:'center',position:'sticky',top:0,background:'white',zIndex:1}}>
          <div style={{display:'flex',gap:'8px'}}>
            {['profile', userRole==='client'?'schedule':null].filter(Boolean).map(v=>(
              <button key={v} onClick={()=>setView(v)}
                style={{padding:'6px 16px',borderRadius:'8px',border:'none',cursor:'pointer',fontSize:'13px',fontWeight:'600',fontFamily:'inherit',
                  background:view===v?'linear-gradient(135deg,#6366f1,#8b5cf6)':'#f1f5f9',
                  color:view===v?'white':'#64748b'}}>
                {v==='profile'?'👤 Profile':'📅 Schedule Interview'}
              </button>
            ))}
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'#94a3b8'}}><X size={20}/></button>
        </div>

        <div style={{padding:'24px'}}>
          {view === 'profile' && (
            <div>
              {/* Candidate header */}
              <div style={{display:'flex',gap:'16px',alignItems:'flex-start',marginBottom:'24px'}}>
                {avatar(candidate.name)}
                <div style={{flex:1}}>
                  <h2 style={{color:'#1e293b',fontSize:'22px',fontWeight:'700',margin:'0 0 4px'}}>{candidate.name}</h2>
                  <div style={{display:'flex',gap:'12px',flexWrap:'wrap'}}>
                    <span style={{color:'#64748b',fontSize:'13px',display:'flex',alignItems:'center',gap:'4px'}}><Briefcase size={13}/> {candidate.experience} yrs exp</span>
                    <span style={{color:'#64748b',fontSize:'13px',display:'flex',alignItems:'center',gap:'4px'}}><DollarSign size={13}/> Rs.{candidate.expectedRate?.toLocaleString()}/month</span>
                    <span style={{color:'#64748b',fontSize:'13px'}}> {candidate.availability}</span>
                  </div>
                </div>
                <span style={{padding:'4px 12px',borderRadius:'20px',fontSize:'12px',fontWeight:'600',
                  background:candidate.status==='shortlisted'?'#dcfce7':candidate.status==='rejected'?'#fee2e2':'#fef9c3',
                  color:candidate.status==='shortlisted'?'#16a34a':candidate.status==='rejected'?'#dc2626':'#ca8a04'}}>
                  {candidate.status?.toUpperCase()}
                </span>
              </div>

              {/* AI Scores */}
              {candidate.aiScore && (
                <div style={{background:'#f8fafc',borderRadius:'12px',padding:'20px',marginBottom:'20px',border:'1px solid #e2e8f0'}}>
                  <h4 style={{color:'#1e293b',fontSize:'14px',fontWeight:'600',margin:'0 0 16px'}}>🤖 AI Interview Scores</h4>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'12px'}}>
                    {[['Overall',candidate.aiScore],['Technical',candidate.technicalScore],['Communication',candidate.communicationScore],['Confidence',candidate.confidenceScore]].map(([label,score])=>(
                      <div key={label} style={{textAlign:'center',padding:'12px',background:scoreBg(score),borderRadius:'8px'}}>
                        <div style={{color:scoreColor(score),fontWeight:'800',fontSize:'22px'}}>{score}</div>
                        <div style={{color:'#64748b',fontSize:'11px',marginTop:'2px'}}>{label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Skills */}
              {candidate.skills?.length > 0 && (
                <div style={{marginBottom:'20px'}}>
                  <h4 style={{color:'#1e293b',fontSize:'14px',fontWeight:'600',margin:'0 0 10px'}}>🛠 Skills</h4>
                  <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
                    {candidate.skills.map(s=><span key={s} style={{padding:'4px 12px',background:'#ede9fe',color:'#6366f1',borderRadius:'20px',fontSize:'12px',fontWeight:'500'}}>{s}</span>)}
                  </div>
                </div>
              )}

              {/* Actions */}
              {(userRole === 'client' || userRole === 'admin') && candidate.status === 'shortlisted' && (
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginTop:'20px',paddingTop:'20px',borderTop:'1px solid #f1f5f9'}}>
                  <button onClick={async()=>{
                    const r = await fetch(`https://talentflow-backend-production-09dd.up.railway.app/api/candidates/report/${candidate._id}`,{headers:{Authorization:`Bearer ${token}`}});
                    const blob = await r.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `Skillora_Report_${candidate.name}.pdf`;
                    a.click();
                  }} style={{padding:'12px',background:'linear-gradient(135deg,#0f172a,#1e1b4b)',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600',fontFamily:'inherit',display:'flex',alignItems:'center',justifyContent:'center',gap:'6px'}}>
                    <FileText size={14}/> 📄 Download AI Report
                  </button>
                  <button onClick={()=>setView('schedule')}
                    style={{padding:'12px',background:'linear-gradient(135deg,#6366f1,#8b5cf6)',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600',fontFamily:'inherit',display:'flex',alignItems:'center',justifyContent:'center',gap:'6px'}}>
                    <Calendar size={14}/> Schedule Interview
                  </button>
                </div>
              )}
            </div>
          )}

          {view === 'schedule' && (
            <ScheduleModal candidate={candidate} token={token} onClose={onClose}/>
          )}
        </div>
      </div>
    </div>
  );
}
